import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput,
  TouchableOpacity, Switch, Alert, ActivityIndicator, Platform,
} from 'react-native';
import { useSelector } from 'react-redux';
import { Ionicons } from '@expo/vector-icons';
import pilotService from '../services/pilotService';
import { canSaveBlock, getPendingPassesForBlock } from './pilotApplicationRecordScreen.utils';

const today = () => {
  const d = new Date();
  return `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getFullYear()}`;
};

const parseDate = (s) => {
  const [d, m, y] = s.split('/');
  return `${y}-${m}-${d}`;
};

const emptyPass = (block, defaultDate = '') => ({
  block_id:         block.id,
  volume_applied_l: '',
  area_covered_ha:  block.area ? String(block.area) : '',
  date:             defaultDate,
  start_time:       '',
  end_time:         '',
  equipment_cleaned: false,
});

export default function PilotApplicationRecordScreen({ route, navigation }) {
  const item = route.params?.item || route.params?.request;
  const { user } = useSelector(s => s.auth);

  const blocks = item?.field_blocks || [];

  // Inicializar passes y saved desde registros existentes
  const initState = () => {
    const existingByBlock = {};
    (item?.execution_records || []).forEach(rec => {
      existingByBlock[rec.block_name] = rec.passes;
    });

    const initialPasses = blocks.map(b => {
      const existing = existingByBlock[b.name];
      if (existing && existing.length > 0) {
        return existing.map(p => ({
          block_id:         b.id,
          volume_applied_l: p.volume_applied_l != null ? String(p.volume_applied_l) : '',
          area_covered_ha:  p.area_covered_ha  != null ? String(p.area_covered_ha)  : (b.area ? String(b.area) : ''),
          date:             p.application_date ? (() => { const [y,m,d] = p.application_date.split('-'); return `${d}/${m}/${y}`; })() : orderDate,
          start_time:       p.start_time  || '',
          end_time:         p.end_time    || '',
          equipment_cleaned: p.equipment_cleaned || false,
        }));
      }
      return [emptyPass(b, orderDate)];
    });

    const initialSaved = blocks.map(b =>
      (item?.execution_records || []).some(r => r.block_name === b.name)
    );

    return { initialPasses, initialSaved };
  };

  // Estado global del formulario
  const orderDate = (() => {
    const raw = item?.confirmed_date || item?.application_date || item?.scheduled_date;
    if (!raw) return today();
    // raw puede ser YYYY-MM-DD o DD/MM/YYYY
    if (raw.includes('-')) {
      const [y, m, d] = raw.split('-');
      return `${d}/${m}/${y}`;
    }
    return raw;
  })();

  const { initialPasses, initialSaved } = initState();

  const [date]                        = useState(orderDate);
  const firstRecord = item?.execution_records?.[0]?.passes?.[0];
  const [droneModel, setDroneModel]   = useState(firstRecord?.drone_model || item?.aircraft?.model || '');
  const [droneSerial, setDroneSerial] = useState(firstRecord?.drone_serial || item?.aircraft?.serial_number || '');
  const [editingAircraft, setEditingAircraft] = useState(false);
  const [myAircraft, setMyAircraft]   = useState([]);

  // Estado por cuartel
  const [activeBlockIdx, setActiveBlockIdx] = useState(null); // cuartel abierto
  const [passes, setPasses]   = useState(initialPasses);
  const [saved, setSaved]     = useState(initialSaved); // cuarteles guardados
  const [savedPasses, setSavedPasses] = useState(initialPasses); // snapshot del último guardado por cuartel
  const [saving, setSaving]   = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    pilotService.getMyAircraft().then(setMyAircraft).catch(() => {});
  }, []);

  const updatePass = (bi, pi, field, value) => {
    setPasses(prev => {
      const next = prev.map(arr => [...arr]);
      next[bi][pi] = { ...next[bi][pi], [field]: value };
      return next;
    });
  };

  const addPass = (bi) => {
    setPasses(prev => {
      const next = prev.map(arr => [...arr]);
      next[bi] = [...next[bi], emptyPass(blocks[bi])];
      return next;
    });
  };

  const removePass = (bi, pi) => {
    setPasses(prev => {
      const next = prev.map(arr => [...arr]);
      if (next[bi].length <= 1) return prev;
      next[bi] = next[bi].filter((_, i) => i !== pi);
      return next;
    });
  };

  const handleSaveBlock = async (bi) => {
    if (!droneModel) { setError('Configura el equipo antes de guardar.'); return; }

    const blockPasses = passes[bi];
    const pendingPasses = getPendingPassesForBlock(blockPasses, savedPasses[bi] || []);
    const hasVol = pendingPasses.some(p => parseFloat(p.volume_applied_l) > 0);
    if (!hasVol) { setError('Ingresa al menos un volumen aplicado.'); return; }

    setSaving(true);
    setError(null);
    try {
      await pilotService.recordBlock(item._raw_id, {
        drone_model:      droneModel,
        drone_serial:     droneSerial,
        pilot_signature:  user?.email || '',
        records: pendingPasses.map(p => ({
          field_block_id:   p.block_id,
          application_date: p.date ? parseDate(p.date) : parseDate(date),
          volume_applied_l: parseFloat(p.volume_applied_l) || 0,
          area_covered_ha:  parseFloat(p.area_covered_ha) || 0,
          start_time:       p.start_time || null,
          end_time:         p.end_time || null,
          equipment_cleaned: p.equipment_cleaned,
        })),
      });
      setSaved(prev => { const n = [...prev]; n[bi] = true; return n; });
      setSavedPasses(prev => { const n = [...prev]; n[bi] = blockPasses.map(p => ({ ...p })); return n; });
      setActiveBlockIdx(null);
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    const anySaved = saved.some(Boolean);
    if (!anySaved) { setError('Debes registrar al menos un cuartel.'); return; }

    const unsaved = blocks.filter((_, i) => !saved[i]);
    if (unsaved.length > 0) {
      const msg = `${unsaved.map(b => b.name).join(', ')} no tienen registro. ¿Completar la misión de todas formas?`;
      if (Platform.OS === 'web') {
        if (!window.confirm(msg)) return;
        doComplete();
      } else {
        Alert.alert('Cuarteles sin registrar', msg, [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Completar', style: 'destructive', onPress: () => doComplete() },
        ]);
      }
    } else {
      doComplete();
    }
  };

  const doComplete = async () => {
    setCompleting(true);
    setError(null);
    try {
      await pilotService.completeDroneMission(item._raw_id);
      if (Platform.OS === 'web') {
        navigation.goBack();
      } else {
        Alert.alert(
          'Misión completada',
          'El registro de aplicación fue guardado correctamente.',
          [{ text: 'OK', onPress: () => navigation.goBack() }]
        );
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al completar. Intenta de nuevo.');
    } finally {
      setCompleting(false);
    }
  };

  const expectedDose = (block) => {
    if (item.rate_type === 'variable') {
      return item.prescription_map?.features?.find(f => f.properties?.field_block_id === block.id) || null;
    }
    return null;
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>

      {/* Equipo */}
      <View style={styles.section}>
        {!editingAircraft ? (
          <View style={styles.aircraftCard}>
            <View style={{ flex: 1 }}>
              <Text style={styles.aircraftLabel}>Equipo asignado</Text>
              <Text style={styles.aircraftName}>{droneModel || 'Sin modelo'}</Text>
              {droneSerial ? <Text style={styles.aircraftSerial}>S/N: {droneSerial}</Text> : null}
            </View>
            <TouchableOpacity onPress={() => setEditingAircraft(true)} style={styles.aircraftEditBtn}>
              <Ionicons name="pencil-outline" size={14} color="#2c3e50" />
              <Text style={styles.aircraftEditText}>Cambiar</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={{ marginTop: 8 }}>
            {myAircraft.length === 0 ? (
              <View style={styles.row}>
                <View style={{ flex: 1 }}>
                  <Field label="Modelo *">
                    <TextInput style={styles.input} value={droneModel} onChangeText={setDroneModel} placeholder="DJI Agras T40" autoFocus />
                  </Field>
                </View>
                <View style={{ width: 12 }} />
                <View style={{ flex: 1 }}>
                  <Field label="N° serie">
                    <TextInput style={styles.input} value={droneSerial} onChangeText={setDroneSerial} placeholder="SN12345" />
                  </Field>
                </View>
              </View>
            ) : (
              myAircraft.map(ac => (
                <TouchableOpacity
                  key={ac.id}
                  style={[styles.aircraftOption, droneModel === ac.model && styles.aircraftOptionSelected]}
                  onPress={() => { setDroneModel(ac.model); setDroneSerial(ac.serial_number); }}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={styles.aircraftOptionName}>{ac.display}</Text>
                    <Text style={styles.aircraftOptionSerial}>S/N: {ac.serial_number}</Text>
                  </View>
                  {droneModel === ac.model && (
                    <Ionicons name="checkmark-circle" size={20} color="#2c3e50" />
                  )}
                </TouchableOpacity>
              ))
            )}
            <TouchableOpacity onPress={() => setEditingAircraft(false)} style={styles.aircraftConfirmBtn}>
              <Text style={styles.aircraftConfirmText}>Confirmar equipo</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Lista de cuarteles */}
      {blocks.map((block, bi) => {
        const isOpen   = activeBlockIdx === bi;
        const isSaved  = saved[bi];
        const dose     = expectedDose(block);
        const blockVol = passes[bi].reduce((s, p) => s + (parseFloat(p.volume_applied_l) || 0), 0);
        const hasPendingChanges = canSaveBlock(passes[bi], savedPasses[bi] || [], saving);

        return (
          <View key={block.id} style={styles.blockSection}>

            {/* Header del cuartel */}
            <TouchableOpacity
              style={[styles.blockHeader, isSaved && styles.blockHeaderDone]}
              onPress={() => setActiveBlockIdx(isOpen ? null : bi)}
              activeOpacity={0.8}
            >
              <View style={[styles.blockStatusDot, isSaved ? styles.dotDone : styles.dotPending]} />
              <View style={{ flex: 1 }}>
                <Text style={styles.blockTitle}>
                  {block.name}
                  {block.area ? <Text style={styles.blockArea}> · {block.area} ha</Text> : null}
                </Text>
                {isSaved && blockVol > 0 ? (
                  <Text style={styles.blockSavedText}>{blockVol.toFixed(1)} L registrados</Text>
                ) : (
                  <Text style={styles.blockPendingText}>Pendiente</Text>
                )}
              </View>
              <Ionicons
                name={isOpen ? 'chevron-up' : 'chevron-down'}
                size={16}
                color={isSaved ? '#16a34a' : '#9ca3af'}
              />
            </TouchableOpacity>

            {/* Formulario del cuartel — solo visible cuando está abierto */}
            {isOpen && (
              <View style={styles.blockForm}>

                {/* Referencia de dosis */}
                <View style={styles.refCard}>
                  {(item.products || []).map((p, i) => {
                    let doseStr = '';
                    if (item.rate_type === 'uniform' && block.area) {
                      const total = (parseFloat(p.dose_per_machine) * parseFloat(block.area)).toFixed(1);
                      doseStr = ` · ${total} ${p.dose_unit} (${p.dose_per_machine} ${p.dose_unit}/ha)`;
                    } else if (dose) {
                      const total = block.area
                        ? ` · ${(parseFloat(dose.properties.dose) * parseFloat(block.area)).toFixed(1)} ${dose.properties.unit.replace('/ha','')}`
                        : '';
                      doseStr = ` · ${dose.properties.dose} ${dose.properties.unit}${total}`;
                    }
                    return (
                      <Text key={i} style={styles.refProduct}>
                        {p.trade_name}
                        <Text style={styles.refDose}>{doseStr}</Text>
                      </Text>
                    );
                  })}
                </View>

                {/* Pasadas */}
                {passes[bi].map((p, pi) => (
                  <View key={pi} style={styles.passCard}>
                    {passes[bi].length > 1 && (
                      <View style={styles.passHeader}>
                        <Text style={styles.passLabel}>Pasada {pi + 1}</Text>
                        <TouchableOpacity onPress={() => removePass(bi, pi)}>
                          <Ionicons name="close-circle-outline" size={16} color="#9ca3af" />
                        </TouchableOpacity>
                      </View>
                    )}
                    <Field label="Fecha">
                      <TextInput
                        style={styles.input}
                        value={p.date}
                        onChangeText={v => updatePass(bi, pi, 'date', v)}
                        placeholder="DD/MM/AAAA"
                      />
                    </Field>
                    <View style={styles.row}>
                      <View style={{ flex: 1 }}>
                        <Field label="Volumen (L)">
                          <TextInput
                            style={styles.input}
                            value={p.volume_applied_l}
                            onChangeText={v => updatePass(bi, pi, 'volume_applied_l', v)}
                            keyboardType="decimal-pad"
                            placeholder="0.0"
                          />
                        </Field>
                      </View>
                      <View style={{ width: 10 }} />
                      <View style={{ flex: 1 }}>
                        <Field label="Superficie (ha)">
                          <TextInput
                            style={styles.input}
                            value={p.area_covered_ha}
                            onChangeText={v => updatePass(bi, pi, 'area_covered_ha', v)}
                            keyboardType="decimal-pad"
                            placeholder="0.0"
                          />
                        </Field>
                      </View>
                    </View>
                    <View style={styles.row}>
                      <View style={{ flex: 1 }}>
                        <Field label="Inicio">
                          <TextInput style={styles.input} value={p.start_time} onChangeText={v => updatePass(bi, pi, 'start_time', v)} placeholder="08:00" />
                        </Field>
                      </View>
                      <View style={{ width: 10 }} />
                      <View style={{ flex: 1 }}>
                        <Field label="Término">
                          <TextInput style={styles.input} value={p.end_time} onChangeText={v => updatePass(bi, pi, 'end_time', v)} placeholder="10:30" />
                        </Field>
                      </View>
                    </View>
                    <View style={styles.switchRow}>
                      <Text style={styles.label}>Equipos limpios</Text>
                      <Switch
                        value={p.equipment_cleaned}
                        onValueChange={v => updatePass(bi, pi, 'equipment_cleaned', v)}
                        trackColor={{ true: '#2c3e50' }}
                      />
                    </View>
                  </View>
                ))}

                {/* Subtotal y acciones */}
                {blockVol > 0 && (
                  <View style={styles.blockTotals}>
                    <Ionicons name="checkmark-circle-outline" size={13} color="#0369a1" />
                    <Text style={styles.blockTotalsText}>
                      <Text style={styles.blockTotalsBold}>{blockVol.toFixed(1)} L</Text>
                      {' · '}
                      <Text style={styles.blockTotalsBold}>
                        {passes[bi].reduce((s, p) => s + (parseFloat(p.area_covered_ha) || 0), 0).toFixed(1)} ha
                      </Text>
                      <Text style={{ color: '#9ca3af' }}> en {passes[bi].length} pasada{passes[bi].length > 1 ? 's' : ''}</Text>
                    </Text>
                  </View>
                )}

                <View style={[styles.row, { gap: 8, marginTop: 10 }]}>
                  <TouchableOpacity style={styles.addPassBtn} onPress={() => addPass(bi)}>
                    <Ionicons name="add" size={14} color="#16a34a" />
                    <Text style={styles.addPassText}>Agregar pasada</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.saveBlockBtn, (saving || !hasPendingChanges) && { opacity: 0.6 }]}
                    onPress={() => handleSaveBlock(bi)}
                    disabled={saving || !hasPendingChanges}
                  >
                    {saving
                      ? <ActivityIndicator size="small" color="#fff" />
                      : <>
                          <Ionicons name="checkmark" size={14} color="#fff" />
                          <Text style={styles.saveBlockText}>Guardar pasada{passes[bi].length > 1 ? 's' : ''}</Text>
                        </>
                    }
                  </TouchableOpacity>
                </View>

              </View>
            )}
          </View>
        );
      })}

      {error && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {/* Botón completar misión */}
      <TouchableOpacity
        style={[styles.completeBtn, completing && { opacity: 0.6 }]}
        onPress={handleComplete}
        disabled={completing}
      >
        {completing
          ? <ActivityIndicator color="#fff" />
          : <>
              <Ionicons name="checkmark-circle" size={18} color="#fff" />
              <Text style={styles.completeBtnText}>Completar misión</Text>
            </>
        }
      </TouchableOpacity>

    </ScrollView>
  );
}

const Field = ({ label, children }) => (
  <View style={{ marginBottom: 12 }}>
    <Text style={styles.label}>{label}</Text>
    {children}
  </View>
);

const styles = StyleSheet.create({
  container:    { flex: 1, backgroundColor: '#f5f7fa' },
  section:      { backgroundColor: '#fff', marginHorizontal: 12, marginTop: 12, borderRadius: 16, padding: 16 },

  aircraftCard:        { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 12, padding: 12, marginTop: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  aircraftLabel:       { fontSize: 10, fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 },
  aircraftName:        { fontSize: 14, fontWeight: '700', color: '#111827' },
  aircraftSerial:      { fontSize: 12, color: '#6b7280', marginTop: 2 },
  aircraftEditBtn:     { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 6, backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#e2e8f0' },
  aircraftEditText:    { fontSize: 12, fontWeight: '600', color: '#2c3e50' },
  aircraftConfirmBtn:  { marginTop: 8, paddingVertical: 10, backgroundColor: '#2c3e50', borderRadius: 10, alignItems: 'center' },
  aircraftConfirmText: { fontSize: 13, fontWeight: '700', color: '#fff' },
  aircraftOption:         { flexDirection: 'row', alignItems: 'center', padding: 12, borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', marginBottom: 6, backgroundColor: '#fff' },
  aircraftOptionSelected: { borderColor: '#2c3e50', backgroundColor: '#f0f4f8' },
  aircraftOptionName:     { fontSize: 13, fontWeight: '600', color: '#111827' },
  aircraftOptionSerial:   { fontSize: 11, color: '#9ca3af', marginTop: 2 },

  blockSection:    { marginHorizontal: 12, marginTop: 8 },
  blockHeader:     { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 14, padding: 14, borderWidth: 1, borderColor: '#f3f4f6', gap: 10 },
  blockHeaderDone: { borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' },
  blockStatusDot:  { width: 10, height: 10, borderRadius: 5 },
  dotPending:      { backgroundColor: '#e5e7eb' },
  dotDone:         { backgroundColor: '#16a34a' },
  blockTitle:      { fontSize: 14, fontWeight: '700', color: '#111827' },
  blockArea:       { fontSize: 12, fontWeight: '400', color: '#9ca3af' },
  blockSavedText:  { fontSize: 11, color: '#16a34a', marginTop: 2 },
  blockPendingText:{ fontSize: 11, color: '#9ca3af', marginTop: 2 },

  blockForm:    { backgroundColor: '#fff', marginTop: 2, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: '#f3f4f6' },

  refCard:    { backgroundColor: '#f8fafc', borderRadius: 10, padding: 10, marginBottom: 10, borderWidth: 1, borderColor: '#e2e8f0' },
  refProduct: { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 2 },
  refDose:    { fontSize: 12, fontWeight: '400', color: '#2c3e50' },

  passCard:   { backgroundColor: '#f9fafb', borderRadius: 12, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: '#f3f4f6' },
  passHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  passLabel:  { fontSize: 12, fontWeight: '600', color: '#6b7280' },

  blockTotals:     { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#f0f9ff', borderRadius: 8, padding: 8, marginBottom: 4, borderWidth: 1, borderColor: '#bae6fd' },
  blockTotalsText: { fontSize: 12, color: '#374151' },
  blockTotalsBold: { fontWeight: '700', color: '#0369a1' },

  addPassBtn:   { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 8, backgroundColor: '#f0fdf4', borderRadius: 10, borderWidth: 1, borderColor: '#bbf7d0' },
  addPassText:  { fontSize: 12, fontWeight: '600', color: '#16a34a' },
  saveBlockBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, backgroundColor: '#2c3e50', borderRadius: 10 },
  saveBlockText:{ fontSize: 13, fontWeight: '700', color: '#fff' },

  label:     { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 5 },
  input:     { backgroundColor: '#fff', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 9, fontSize: 14, color: '#111827' },
  row:       { flexDirection: 'row' },
  switchRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4 },

  errorBox:  { marginHorizontal: 12, marginTop: 8, padding: 12, backgroundColor: '#fef2f2', borderRadius: 12, borderWidth: 1, borderColor: '#fecaca' },
  errorText: { color: '#dc2626', fontSize: 13 },

  completeBtn:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, margin: 16, marginTop: 12, backgroundColor: '#16a34a', borderRadius: 14, padding: 16 },
  completeBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
});
